/**
 * 거래 일괄 입력(A-7) — 저장 직전 재검증에 쓸 마스터 데이터 로더
 *
 * @why 저장 직전에 마스터를 다시 읽는 이유:
 * 입력하는 동안 다른 사용자가 결제수단·카테고리·멤버를 바꿀 수 있다.
 * 화면의 선택지는 열었을 때의 스냅샷이라, 최신 값으로 다시 검증해야
 * 이미 사라진 항목을 참조한 거래가 저장되는 것을 막는다.
 *
 * @why 훅의 refetch가 아니라 repository를 직접 부르는 이유:
 * refetch는 훅 내부 상태만 갱신하고 값을 돌려주지 않아, 호출 직후에는
 * 아직 갱신 전 값이 보인다. 검증에는 "지금 막 읽은 값"이 필요하다.
 *
 * @why 페이지 컴포넌트가 아니라 이 모듈에 두는 이유:
 * 어떤 질의를 쓰는지가 곧 검증 결과를 좌우하는데(아래 참조), 화면 안에 묻어 두면
 * 테스트로 고정할 수 없다. 실제로 이 부분이 어긋나 버그가 났다.
 *
 * ---
 *
 * ⚠️ **여기서 반드시 지켜야 하는 규칙: 화면이 쓴 질의와 같은 질의를 쓸 것.**
 *
 * "최신 값"이어야 하지, "다른 값"이어서는 안 된다.
 * 과거에 이 로더가 `userRepository.findAll()`을 썼다가 다음 버그가 났다:
 *
 *   - 화면의 사람 목록은 `findActive()`로 만든다. 이 메서드는 시트에 없는
 *     **합성 사용자 '공용'(user_shared)** 을 맨 앞에 끼워 넣는다.
 *   - `findAll()`은 시트를 그대로 읽으므로 '공용'이 없다.
 *   - 공용 카드를 고르면 spenderId가 'user_shared'가 되는데,
 *     화면 검증은 통과하고 저장 검증만 "존재하지 않는 멤버입니다"로 실패했다.
 *   - 사용자에게는 **빨간 표시가 하나도 없는데 오류 때문에 저장이 막히는** 상태로 보였다.
 *
 * findAll ⊇ findActive라고 생각하기 쉽지만, 사용자만은 반대다(findActive가 더 크다).
 * 새 마스터 데이터를 추가할 때도 화면 훅이 쓰는 메서드를 그대로 따라갈 것.
 */

import { categoryRepository } from '@/domains/category/repositories'
import { paymentMethodRepository } from '@/domains/payment/repositories'
import { userRepository } from '@/domains/user/repositories'
import type { BulkValidationContext } from './bulkTransactionValidator'

export async function loadBulkValidationContext(): Promise<BulkValidationContext> {
  const [categories, paymentMethods, users] = await Promise.all([
    // useCategories는 findByType('expense')+findByType('income')를 쓴다.
    // 둘 다 findAll에서 파생되고 합집합이 findAll과 같으므로 여기서는 findAll 1회로 충분하다.
    // (findAll이 이미 isActive=false를 걸러내고 계층 구조까지 만들어 준다)
    categoryRepository.findAll(),
    // usePaymentMethods와 동일하게 findActive
    paymentMethodRepository.findActive(),
    // useUsers와 동일하게 findActive — '공용'(user_shared)이 여기에만 들어 있다
    userRepository.findActive(),
  ])

  return { categories, paymentMethods, users }
}
